// @ts-check
/* global $ module require plupload */
'use_strict';

declare var $;
declare var Tygh;
declare var plupload;

import { GenericUploadQueue } from './generic_upload_queue';
import { MiniCustomerRegistration } from '../tv_users/users';
import { Notifications } from '../tv_utils/notifications';
import { get_form_data } from '../tv_utils/utils.js';

export class DIFUUploadQueue extends GenericUploadQueue {

    /**
     * * Included from GenericUploadQueue.
     * @var {[key: string]: any} form_elements dict of related HTMLElements on the dom.
     * @var {string} dispatch url to call once to upload files.
     * @var {string} container changes based on difu.
     *
     * * Additional variables needed for Design It For You.
     * @var {string} uploader_id is the Unique ID for each Design to Upload for.
     * @var {string} order_id is either the Config ID or Inventtrans ID.
     * @var {number} sales_id is needed for an Order and also determines if this is from an order.
     * @var {boolean} from_order determines if this is from a product or from an order.
     * @var {boolean} from_dt determines if this is from the Design Tool.
     * @var {boolean} skip_add_to_cart_modal flag to determine if we're skipping the add to cart modal on product page.
     * @var {any} upload_queue is the plupload queue initialized.
     * @var {[key: string]: any} difu_data is the data needed for the Design It For You feature.
     * @var {number} items_to_upload is a flag for and number of items needed to upload.
     * @var {number} design_id is the ID of the Design from the Design Tool.
     * @var {string} message is the validated message from the Customer.
     * @var {[key: string]: any} guest_request dict of HTMLElements related to guest requests.
     * @var {boolean} hidden_instructions flag to determine if the Design Instructions are visible or not.
     * @var {string} difu_id is the Unique ID for this Design Service Request.
     * @var {boolean} is_summary determines if the DIFU uploader is a summary or not
     * @var {boolean} single_design a flag indicating whether there are multiple difu designs.
     * @var {NodeJS.Timer} init_timeout to allow for cancellation of init call if removed before loaded.
     */
    form_elements: { [key: string]: any };
    dispatch: string;
    container: string = 'upload_queue';

    uploader_id: string = '';
    order_id: string = '';
    sales_id: number | string;
    from_order: boolean = false;
    from_dt: boolean = false;
    skip_add_to_cart_modal: boolean = false;
    image_index: number = 0;
    upload_queue: any;
    difu_data: { [key: string]: any } = {};
    items_to_upload: number = 0;
    design_id: number = 0;
    message: string = '';
    guest_request: { [key: string]: any } = {};
    hidden_instructions: boolean = true;
    difu_id: string = '';
    is_summary: boolean = false;
    single_design: boolean;
    init_timeout: NodeJS.Timer;

    /**
     * @override
     * Overriding the Constructor to include information necessary for Design It For You.
     *
     * @param {string} uploader_id the ID the uploader looks for to initialize.
     * @param {number} sales_id is needed for an Order and also determines if this is from an order.
     * @param {boolean} single_design a flag indicating whether there are multiple difu designs.
     */
    constructor(uploader_id: string, sales_id: number | string = 0, single_design: boolean = false) {
        super();
        this.is_summary = uploader_id.includes('summary');
        this.form_elements = {};
        this.dispatch = `${window.location.origin}/index.php?dispatch=tv_artwork.upload_design_it_for_you`;
        this.uploader_id = uploader_id;
        this.difu_id = this.uploader_id;

        this.container = `${this.container}_${this.uploader_id}`;
        // Return early if queue container doesn't exist on the page.
        if (!document.getElementById(this.container)) {
            return;
        }

        this.sales_id = sales_id;
        this.from_order = this.sales_id ? true : false;
        this.from_dt = document.getElementById('js-dt-design-id') ? true : false;

        // Check if this Request has Multiple Designs.
        const image_index = <HTMLInputElement>document.getElementById('js-difu-index-' + this.uploader_id);
        if (image_index) {
            // If there are Multiple Designs, then we need the Image Index and change up the DIFU ID.
            this.image_index = parseInt(image_index.value, 10);
            // Gotta do RegExp stuff to replace from the end of the string.
            this.difu_id = this.difu_id.replace(new RegExp(`-${image_index.value}$`), '');
        }
        // This might be from the Summary on Product Pages.
        this.difu_id = this.difu_id.replace(new RegExp(`-summary$`), '');
        this.order_id = this.difu_id;

        // If Artwork has already been chosen for this Design, then we need to provide the ability to change that.
        const artwork_chosen = document.getElementById(`tv-difu-chosen-${this.uploader_id}`);
        const change_artwork = document.getElementById(`tv-difu-change-${this.uploader_id}`);
        if (artwork_chosen && change_artwork) {
            artwork_chosen.addEventListener('click', () => {
                change_artwork.classList.remove('hidden');
                artwork_chosen.parentElement.parentElement.classList.add('hidden');
            });
        }

        // Only do this for the first index.
        if (this.image_index === 0) {
            // Check for the Artwork for Designs Modal.
            const art_design_modal_openers = document.getElementsByClassName(`js-difu-for-configuration-${this.difu_id}`);
            for (const art_opener of art_design_modal_openers) {
                // The click event needs to close the Artwork for Designs Modal before opening the DIFU Uploader.
                art_opener.addEventListener('click', () => {
                    document.getElementById(`js-quantity-designs-modal-closer-${this.difu_id}`).click();
                    (<HTMLElement>document.getElementsByClassName(
                        `js-design-it-for-you-modal-opener-${this.difu_id}`
                    )[0]).click();
                });
            }
        }

        this.single_design = single_design || this.uploader_id === this.difu_id;

        // Set a timeout for the init call to allow for cancellation if removed before loaded.
        this.init_timeout = setImmediate(() => {
           super.init_queue();
           this.__bind();
        }); 
    }

    /**
     * New bind method to change class variables to DIFU based variables.
     *
     * @returns {void}
     */
    __bind(reload: boolean = false): void {
        // pluploadQueue.
        this.upload_queue = $(`#${this.container}`).pluploadQueue();

        this.form_elements.uploader_container = document.getElementById(this.container);
        this.form_elements.message = document.getElementById(`uploader_message_${this.uploader_id}`);
        this.form_elements.button_container = document.querySelector(`#${this.container} .plupload_buttons`);
        this.form_elements.upload_status = document.querySelector(`#${this.container} .plupload_upload_status`);
        this.form_elements.custom_validation_message = document.getElementById('js-custom-validation-message');
        this.form_elements.filelist_container = document.getElementById(`${this.container}_filelist`);
        this.form_elements.close = document.querySelector(`#difu-button-cancel-${this.difu_id}`);
        this.form_elements.start_upload = document.querySelector(`#${this.container} .plupload_start`);

        // Hide the buttons for custom UI effects and Upload Logic.
        this.form_elements.add_files = document.getElementById(`${this.container}_browse`);
        this.form_elements.add_files.style.display = 'none';

        // add_files is an anchor element with href="#". Clicking an anchor tag with href="#" navigates
        // to the top of the page by default, so we want to remove this attribute.
        this.form_elements.add_files.removeAttribute('href');

        // Add items to the Items to Upload.
        this.upload_queue.bind('FilesAdded', (up, files) => {
            // Need to be sure the file wasn't filtered out.
            if (up.files.length > 0) {
                this.items_to_upload += 1;
            }

            this.adjust_select_files();
        });

        // Reduce the number of Items to Upload.
        this.upload_queue.bind('FilesRemoved', () => {
            this.items_to_upload -= 1;

            if (this.items_to_upload < 0) {
                this.items_to_upload = 0;
            }

            this.adjust_select_files();
        })

        this.form_elements.filelist_container.classList.add('tv-height__size-100px');

        // Removing tooltip.
        document.getElementById(`${this.container}_container`).removeAttribute('title');

        // If reloading after upload failure, remaining events need not be rebound.
        if (reload) {
            return;
        }

        // Make the entire Plupload Queue a button.
        this.form_elements.uploader_container.addEventListener('click', (e: Event) => {
            // Check if event is trusted to prevent infinite click loop.
            if (e.isTrusted) {
                this.form_elements.add_files.click();
                // Keep the Modal from jumping around.
                const dialog = $.ceDialog('get_last')[0];
                if (dialog) {
                    $.ceDialog('get_last').ceDialog('reload');
                }
            }
        });

        const openers = document.getElementsByClassName(`js-design-it-for-you-modal-opener-${this.difu_id}`);
        this.adjust_select_files();
        for (const opener of openers) {
            opener.addEventListener('click', this.adjust_select_files.bind(this));
        }
        this.form_elements.submitted_message = document.getElementById(`js-submitted-message-${this.uploader_id}`);

        // MiniCustomerRegistration validates input, email, and password fields.
        this.guest_request.step_one = document.getElementById(`js-difu-guest-step-one-${this.difu_id}`);

        // Dont add MiniCustomerRegistration for difu uploader summary
        if (!this.is_summary) {
            this.guest_request.guest_registration = new MiniCustomerRegistration(null, () => {
                // Adjust the Multiple Designs Uploader Lists.
                const uploader_list = document.getElementById(`js-difu-uploader-${this.uploader_id}`).parentElement;
                if (uploader_list) {
                    uploader_list.classList.remove('tv-max-height__49-vh');
                    uploader_list.classList.add('tv-max-height__74-vh');
                }

                this.guest_request.step_one.classList.add('hidden');
                if (this.form_elements.close) {
                    // Don't close Modal.
                    this.form_elements.close.removeAttribute('onClick');
                    // Instead, refresh the page to update Customer Login.
                    this.form_elements.close.addEventListener('click', () => {
                        document.location.reload();
                    });
                }
            }, this.difu_id);
        }

        // Bind the Add to Cart button for the Product Page.
        this.form_elements.add_to_cart = document.getElementById(`difu-button-add-to-cart-${this.uploader_id}`);
        if (this.form_elements.add_to_cart) {
            this.skip_add_to_cart_modal = true;
            this.form_elements.add_to_cart.addEventListener('click', () => {
                // We don't want to Register if the Customer is already signed in.
                if (this.guest_request.step_one.classList.contains('hidden')) {
                    // Message validation is done in Validate DIFU function.
                    if (this.validate_difu()) {
                        this.add_to_cart();
                    }
                } else {
                    // Validate DIFU inputs first.
                    const valid_message = this.validate_difu();
                    // Then Validate Registration inputs.
                    if (valid_message && this.guest_request.guest_registration.validate()) {
                        // The attempt to Register the Customer.
                        this.guest_request.guest_registration.register('Registering Contact Info', false, () => {
                            if (valid_message) {
                                this.add_to_cart();
                            }
                        });
                    }
                }
            });
        }

        // Bind the Design Tool Finish button.
        this.form_elements.dt_finish = document.getElementById(`difu-button-finish-${this.uploader_id}`);
        if (this.form_elements.dt_finish) {
            this.form_elements.dt_finish.addEventListener('click', () => {
                this.design_id = parseInt((<HTMLInputElement>document.getElementById('js-dt-design-id')).value, 10);
                if (!this.guest_request.step_one.classList.contains('hidden')) {
                    // Validate inputs, email, and password fields and try to register guest.
                    if (this.guest_request.guest_registration.validate()) {
                        this.guest_request.guest_registration.register('Registering Contact Info', false, () => {
                            (this.items_to_upload) ? this.form_elements.start_upload.click() : this.prepare_message();
                        });
                    }
                } else if (this.items_to_upload) {
                    this.form_elements.start_upload.click();
                } else {
                    this.prepare_message();
                }
            });
        }

        // Otherwise, this is from Upload Artwork, Order Details, or Cart.
        this.form_elements.submit_button = document.getElementById(`difu-button-submit-${this.uploader_id}`);
        if (this.form_elements.submit_button) {
            this.form_elements.submit_button.addEventListener('click', () => {
                if (!this.guest_request.step_one.classList.contains('hidden')) {
                    // Validate inputs, email, and password fields and try to register guest.
                    if (this.guest_request.guest_registration.validate()) {
                        this.guest_request.guest_registration.register('Registering Contact Info', false, () => {
                            (this.items_to_upload) ? this.form_elements.start_upload.click() : this.prepare_message();
                        });
                    }
                } else if (this.items_to_upload) {
                    this.form_elements.start_upload.click();
                } else {
                    this.prepare_message();
                }
            });
        }

    }

    /**
     * Method to Customize Select Files such as Customizing the Drop Text and
     * stopping propagation (or bubbling) on the event of removing a file.
     *
     * @return {void}
     */
    adjust_select_files(): void {
        // Get container height.
        const container_height = this.form_elements.filelist_container.offsetHeight;

        // Get last child element position and height.
        const last_child = <HTMLElement>this.form_elements.filelist_container.lastChild;
        var min_height = 0;
        var vertical_offset = 0;
        if (last_child) {
            min_height = last_child.offsetHeight;
            vertical_offset = last_child.offsetTop + min_height;
        }

        // Calculate max height of plus symbol.
        const height = (container_height - vertical_offset);

        // If there are Items to Upload, add the plus sign.
        if (this.items_to_upload) {
            // Add Plus symbol.
            this.form_elements.filelist_container.insertAdjacentHTML(
                'beforeend',
                `<div class="tv-display__flex tv-width__half tv-margin__auto"
                    style="height:${height}px;min-height:${min_height}px;"
                >
                    <div class="tv-margin__auto tv-text-align__center"><i class="ty-icon-plus"></i></div>
                </div>`
            );
        } else { // Otherwise we need to have the Drag or Click to add Files.
            // Adding custom Drop Text.
            this.form_elements.filelist_container.insertAdjacentHTML(
                'beforeend',
                `<div class="tv-display__flex tv-width__half tv-margin__auto"
                    style="height:${height}px;min-height:${min_height}px;"
                >
                    <div class="tv-margin__auto tv-text-align__center tv-file__upload-text">Drag files here or Click to add files.</div>
                    <i class="fas fa-upload fa-5x tv-file__upload"></i>
                </div>`
            );
        }

        // Add click event to Remove File button to stop it from firing the Browse for File.
        for (const remove_file of document.querySelectorAll('.plupload_file_action a')) {
            remove_file.addEventListener('click', event => {
                event.stopPropagation();
            });
        }
    }

    /**
     * Method to validate the message and adjust the UI accordingly.
     *
     * @return {boolean} is the flag for if the message is valid.
     */
    validate_difu(): boolean {
        // Trim the message.
        this.message = this.form_elements.message.value.trim();
        this.form_elements.message.value = this.message;

        // Validate the message.
        if (this.message.length < 1) {
            if (this.items_to_upload) {
                this.form_elements.message.setCustomValidity(this.form_elements.custom_validation_message.value);

                this.form_elements.button_container.style.display = 'inline';
                this.form_elements.upload_status.style.display = 'inline';

                this.upload_queue.state = plupload.STOPPED;
                this.upload_queue.status = plupload.QUEUED;
                this.upload_queue.trigger('StateChanged');
            }

            this.form_elements.message.reportValidity();
            return false;
        }

        if (this.form_elements.email && !this.form_elements.email.reportValidity()) {
            return false;
        }

        // Get the data for the DIFU.
        this.difu_data = {
            'message': this.message,
            'from_dt': this.from_dt,
            'sales_id': this.from_order ? this.sales_id : '',
            'inventtrans_id': this.from_order ? this.order_id : '',
            'configuration_id': !this.from_order ? this.order_id : '',
            'index': this.image_index,
            'design_id': this.design_id
        };

        if (this.guest_request.step_one && !this.guest_request.step_one.classList.contains('hidden')) {
            this.difu_data.firstname = this.guest_request.guest_registration.inputs.firstname.value;
            this.difu_data.email = this.guest_request.guest_registration.inputs.email.value;
        }

        return true;
    }

    /**
     * Prepare the message the Customer wants to submit.
     *
     * @param {event} e is the event that may have been triggered.
     * @returns {void}
     */
    prepare_message(e = null): void {
        // Validate the message first.
        if (!this.validate_difu()) {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }
            return;
        }

        /**
         * If the customer is just submitting a message,
         * then we need to get rid of the uploader.
         */
        const uploader_container = document.getElementById(`${this.container}`);
        uploader_container.innerHTML = '';
        uploader_container.classList.add('hidden');

        this.submit_message();
    }

    /**
     * @override
     * A complete Override of the Validate Upload method to pass Design It For You specific information.
     *
     * @returns {boolean}
     */
    validate_upload(): boolean {
        if (!this.validate_difu()) {
            return false;
        }

        // Set parameters for the artwork Upload.
        this.upload_queue.settings.multipart_params = this.difu_data;

        // Toggle the Status Box to show.
        $.toggleStatusBox('show', {
            overlay: true,
            statusContent: '<span class="ty-ajax-loading-box-with__text-wrapper">Uploading Artwork</span>',
            statusClass: 'ty-ajax-loading-box_text_block'
        });
    }

    /**
     * The product needs to be added to the cart before
     * we can create a Design Tool design for it.
     *
     * @return {void}
     */
    add_to_cart() {
        // Check if we're on the Product Page with Multiple Designs and the Product is in the Cart.
        const cart_checker = <HTMLInputElement>document.getElementById(`js-difu-cart-checker-${this.difu_id}`);
        if (cart_checker && cart_checker.value) {
            if (this.items_to_upload) {
                this.form_elements.start_upload.click();
            } else {
                this.submit_message();
            }
            return;
        }
        const product_id = (<HTMLInputElement>document.getElementById('product_id')).value;

        // Send it off to the checkout and then the Design Tool in the callback.
        $.ceAjax('request', `index.php?dispatch=checkout.add..${product_id}`, {
            'full_render': true,
            'method': 'POST',
            'caching': false,
            'data': get_form_data('product_form'),
            'message': 'Preparing to Submit Request',
            'callback': response => {
                // In the case of Multiple Designs, we don't want to close the Modal yet.
                if (response.notifications && response.notifications.length === 0) {
                    if (cart_checker) {
                        cart_checker.value = 'true';
                    }
                    // Show Product Summary.
                    if (!this.skip_add_to_cart_modal) {
                        $(`#js-hidden-config-summary-${this.difu_id}`).click();
                    }
                    // Handle Response.
                    if (this.items_to_upload) {
                        this.form_elements.start_upload.click();
                    } else {
                        this.submit_message();
                    }
                }

                // notification type (E - error, W - warning, N - notice, O - order error on checkout, I - information)
                for (const [key, notification] of Object.entries(response.notifications)) {
                    if (notification['type'] === 'I') {
                        if (cart_checker) {
                            cart_checker.value = 'value';
                        }
                        // Change notification Title.
                        response.notifications[key].title = Tygh.tr('tv_difu_request_submitted_title');
                        // Close the modal to show the Add to Cart Notification.
                        $(`#js-design-it-for-you-modal-${this.difu_id}`).prev(
                            `div.ui-dialog-titlebar`
                        ).children(`[title*='Close']`)[0].click();
                        // Show Product Summary.
                        $(`#js-hidden-config-summary-${this.difu_id}`).click();

                        // Handle Response.
                        if (this.items_to_upload) {
                            this.form_elements.start_upload.click();
                        } else {
                            this.submit_message();
                        }
                    }
                };
            }
        });
    }

    /**
     * Method to Submit the DIFU to PHP Controller.
     *
     * @return {void}
     */
    submit_message() {
        // AJAX request to submit the DIFU.
        $.ceAjax('request', 'index.php?dispatch=tv_artwork.submit_description_design_it_for_you', {
            'full_render': false,
            'method': 'POST',
            'caching': false,
            'data': this.difu_data,
            'message': 'Submitting Design Service Request',
            'callback': this.set_difu_record_data.bind(this)
        });
    }

    /**
     * Sends DIFU data to PHP Controller to set record data.
     * This function ensures that upload data (e.g., design_id) can be accessed
     * in both the submit_message and upload_complete callbacks.
     * @param {object} response from the previous Ajax request that may contain the file path.
     * @return {void}
     */
    set_difu_record_data(response = null): void {
        if (response && response.file_path) {
            // Need to grab the File Path.
            this.difu_data.file_path = response.file_path;
        }

        // AJAX request to set record data for artwork upload.
        $.ceAjax('request', 'index.php?dispatch=tv_artwork.set_difu_record_data', {
            'full_render': false,
            'method': 'POST',
            'caching': false,
            'data': this.difu_data,
            'message': 'Adding Record Information',
            'callback': data => {
                this.design_id = data.design_id;

                // Close DIFU Modal for single design on product page
                if (this.form_elements.add_to_cart && this.single_design) {
                    $.ceDialog('get_last').ceDialog('close');                    
                }
                this.callback_handler();
            }
        });

    }

    /**
     * @override
     * Calls parent method Upload Complete, then adds extra logic for Design It For You.
     *
     * @param {plupload.Uploader} uploader_instance for plupload.
     * @param {array} files array of the files that have been uploaded.
     * @returns {void}
     */
    upload_complete(uploader_instance, files): void {
        $.toggleStatusBox('hide');
        let error_found = false;
        // Check to see if the file failed upload and display an error notification.
        for (const file of files) {
            if (file.status === plupload.FAILED) {
                Notifications.set_notification(
                    'E',
                    'Failed to upload artwork.',
                    `An error occurred when uploading ${file.name}`,
                    false
                );
                error_found = true;
                uploader_instance.removeFile(file);
                $.ceDialog('get_last').ceDialog('reload');
                super.init_queue();
                this.__bind(true);
                this.adjust_select_files();
            }
        }

        if (!error_found) {
            // If no error occurred during upload, then hide the submit button.
            this.set_difu_record_data();
        }
    }

    /**
     * Callback Handler for it's from the Order or Artwork page.
     *
     * The Product Page causes the modal to close and
     * the Design Tool redirects to the Cart.
     *
     * @returns {void}
     */
    callback_handler(): void {
        // If this is from the Design Tool and a Single Design.
        if (this.form_elements.dt_finish && this.single_design) {
            // Hide the registration fields, and reCaptcha.
            if (this.guest_request.step_one) { // registration fields.
                this.guest_request.step_one.classList.add('hidden');
            }
            // reCaptcha.
            $(`#js-difu-guest-step-two-${this.difu_id} .g-recaptcha`).addClass('hidden');

            // We need to redirect to the Cart.
            let dispatch = 'index.php?dispatch=checkout.cart';
            // If this was from an order we need to go to the Order Page.
            if (this.sales_id) {
                dispatch = `index.php?dispatch=orders.details&sales_id=${this.sales_id}`;
            }
            window.location.replace(`${window.location.origin}/${dispatch}`);

        } else if (this.form_elements.add_to_cart && this.single_design) {
            // From Product Page and Single Design.

            if (this.skip_add_to_cart_modal) { // Immediately redirect to cart
                $.ceDialog('get_last').ceDialog('close');
                // Wait a fraction of a second to allow modals to finish closing
                // (otherwise the new spinner gets hidden immediately).
                setTimeout(() => {
                    $.toggleStatusBox('show', {
                        overlay: true,
                        statusContent: '<span class="ty-ajax-loading-box-with__text-wrapper">Adding product to cart</span>',
                        statusClass: 'ty-ajax-loading-box_text_block'
                    });
                    window.location.replace(`${window.location.origin}/index.php?dispatch=tv_cart.added_to_cart`);
                }, 100);

            } else { // Use 'add to cart' modal
                // If this is a Guest Request, then we want to refresh the page afterwards.
                if (this.guest_request.step_one && !this.guest_request.step_one.classList.contains('hidden')) {
                    (<HTMLElement>document.querySelector('span.close')).style.display = 'none';
                    (<HTMLElement>document.querySelector('.ty-btn.cm-notification-close')).addEventListener('click', () => {
                        document.location.reload();
                    });
                }

                // Reveal DIFU submitted message.
                document.getElementById('js-difu-submitted').classList.remove('hidden');

                // Hide artwork buttons on the Product Summary.
                const summary_art_btns = document.getElementById('js-config-summary-artwork-buttons');
                if (summary_art_btns) {
                    summary_art_btns.classList.add('hidden');
                }
                const summary_templates = document.getElementById('js-config-summary-artwork-templates');
                if (summary_templates) {
                    summary_templates.classList.add('hidden');
                }
            }

        } else { // From Order Page, Artwork Page, or Cart Page.
            // Hide the text box, instructions, registration fields, reCaptcha, and art uploader.
            this.form_elements.message.parentNode.classList.add('hidden');
            if (this.single_design) {
                $(`#js-modal-instructions-${this.difu_id}`).addClass('hidden');
            }
            $(`#js-required-input-${this.uploader_id}`).addClass('hidden');
            if (this.guest_request.step_one) { // Registration Fields.
                this.guest_request.step_one.classList.add('hidden');
            }
            // reCaptcha.
            $(`#js-difu-guest-step-two-${this.difu_id} .g-recaptcha`).addClass('hidden');
            // Art Uploader.
            $(`#upload_queue_${this.uploader_id}`).addClass('hidden');
            $(`#js-design-it-for-you-modal-${this.difu_id} .ty-control-group__title`).addClass('hidden');

            // Disable the Email if available.
            if (this.form_elements.email) {
                this.form_elements.email.disabled = true;
            }

            // Add the entered message into a display box.
            this.form_elements.submitted_message.innerHTML = this.message;
            this.form_elements.submitted_message.parentNode.classList.remove('hidden');

            // Show the Design ID
            $(`#js-design-id-${this.uploader_id}`).text(this.design_id);

            // Hide Submit button.
            if (this.form_elements.add_to_cart) {
                this.form_elements.add_to_cart.classList.add('hidden');
            } else if (this.form_elements.dt_finish) {
                this.form_elements.dt_finish.classList.add('hidden');
            } else if (this.form_elements.submit_button) {
                this.form_elements.submit_button.classList.add('tv-display__none');
            }

            if (this.form_elements.close) {
                // Don't close Modal.
                this.form_elements.close.removeAttribute('onClick');
                $(`#js-design-it-for-you-modal-${this.difu_id}`).prev(
                    `div.ui-dialog-titlebar`
                ).children(`[title*='Close']`)[0].style.display = 'none';

                // If this is Multiple Designs in the Design Tool.
                if (this.form_elements.dt_finish) {
                    // We need to redirect to the Cart.
                    let dispatch = 'index.php?dispatch=checkout.cart';
                    // If this was from an order we need to go to the Order Page.
                    if (this.sales_id) {
                        dispatch = `index.php?dispatch=orders.details&sales_id=${this.sales_id}`;
                    }
                    this.form_elements.close.addEventListener('click', () => {
                        window.location.replace(`${window.location.origin}/${dispatch}`);
                    });
                } else {
                    // Instead, refresh the page.
                    this.form_elements.close.addEventListener('click', () => {
                        // Show loading spinner as refresh can take some time.
                        $.toggleStatusBox('show', {
                            overlay: true,
                            statusContent: '<span class="ty-ajax-loading-box-with__text-wrapper">Loading</span>',
                            statusClass: 'ty-ajax-loading-box_text_block'
                        });
                        document.location.reload();
                    });
                }
            }
        }

        // Keep the Modal from jumping around.
        const dialog = $.ceDialog('get_last')[0];
        if (dialog) {
            $.ceDialog('get_last').ceDialog('reload');
        }

        // Adjust the Multiple Designs Uploader Lists.
        const uploader_list = document.getElementById(`js-difu-uploader-${this.uploader_id}`).parentElement;
        if (uploader_list) {
            uploader_list.classList.remove('tv-max-height__49-vh');
            uploader_list.classList.add('tv-max-height__74-vh');
        }
    }

    /**
     * File Uploaded Method to allow us to capture info about the file.
     * The Response from PHP will be in info.response.
     * @param {plupload.Uploader} uploader Uploader instance sending the event.
     * @param {plupload.File} file Another file that has to be added to the queue.
     * @param {Object} info is about the request such as its response, status, and headers.
     * @return {void}
     */
    file_uploaded(up, file, info) {
        const response = JSON.parse(info.response);
        if (response && response.result && response.result.file_path) {
            // Need to grab the File Path.
            this.difu_data.file_path = response.result.file_path;
        }
    }

    /**
     * Cancels initialization for the upload queue.
     *
     * @returns {void}
     */
     cancel_initialization() {
        clearImmediate(this.init_timeout);
    }

}
